/**
 * SedRebuildBanner — SED_Architecture_Unification SA4 + SA5.
 *
 * Rendered on both the Site Evidence tab (SiteLandingPage) and the
 * Report viewer Evidence section (SiteRagReportView). One canonical
 * banner so the user always sees the same in-flight signal regardless
 * of which surface they're on.
 *
 * States:
 *   - sedStatus === 'bootstrapping' AND fresh → blue info banner
 *     with N/total progress + start time, role="status", aria-live="polite".
 *   - sedStatus === 'bootstrapping' AND a stale heartbeat → yellow
 *     "still working — you can keep waiting" soft-stall banner with
 *     Force-refresh + Cancel-and-retry. There is deliberately NO alarming
 *     red "appears stuck" tier: SED builds legitimately run long, so a
 *     5-min no-progress alarm is a false positive. Genuinely-dead jobs are
 *     force-failed by the 30-min backstop in AuthGate (runStuckBootstrapBackstop).
 *   - anything else → renders nothing.
 *
 * Pure presentation — reads site.sedRebuildProgress + sedStatusStartedAt
 * straight off the site doc; doesn't subscribe to the store itself so
 * it stays cheap to render inside both parent components which already
 * subscribe to their own slices.
 */
import React from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../../store';
import { Button } from '../ui/button';
import {
  isHeartbeatStale,
  isStuckBootstrap,
  isAbandonedBootstrap,
  shouldForceAbortBootstrap,
  heartbeatStaleThresholdMs,
} from '../../lib/sedStaleness';
import type { SiteEntity } from '../../types/site';

interface Props {
  site: SiteEntity;
}

function formatRelative(iso: string | undefined, nowMs: number): string {
  if (!iso) return 'just now';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'just now';
  const deltaMs = Math.max(0, nowMs - t);
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

/**
 * Stage-aware progress bar for an in-flight SED rebuild.
 *
 * Determinate when `total > 0 && done > 0` — width = done/total. Otherwise
 * INDETERMINATE (an animated sliver): at "0/4" the heavy first stage simply
 * hasn't ticked a milestone yet, so a 0%-filled bar would read as "frozen" —
 * the exact false signal that made a still-working build look stuck. The
 * indeterminate bar communicates "working" without asserting a false number.
 *
 * `role="progressbar"` + aria-value* keeps it legible to assistive tech,
 * matching the banner's existing role="status"/aria-live posture.
 */
const SedProgressBar: React.FC<{ done: number; total: number; tone?: 'info' | 'warning' }> = ({
  done,
  total,
  tone = 'info',
}) => {
  const determinate = total > 0 && done > 0;
  const pct = determinate ? Math.min(100, Math.max(0, Math.round((done / total) * 100))) : null;
  const fill = tone === 'warning' ? 'bg-warning' : 'bg-info';
  const track = tone === 'warning' ? 'bg-warning/15' : 'bg-info/15';
  return (
    <div
      role="progressbar"
      aria-label="SED rebuild progress"
      aria-valuemin={0}
      {...(determinate ? { 'aria-valuemax': total, 'aria-valuenow': done } : {})}
      className={`h-1 w-full overflow-hidden rounded-full ${track}`}
      data-testid="sed-rebuild-progress-bar"
    >
      {pct == null ? (
        // Indeterminate: a pulsing sliver that says "working" with no false %.
        <div className={`h-full w-1/3 ${fill} animate-pulse rounded-full`} />
      ) : (
        <div
          className={`h-full ${fill} rounded-full transition-[width] duration-500 ease-out`}
          style={{ width: `${pct}%` }}
        />
      )}
    </div>
  );
};

export const SedRebuildBanner: React.FC<Props> = ({ site }) => {
  // Tick every 15s while the banner is in flight so the "started Nm
  // ago" / "no progress for Nm" copy stays accurate. The 15s cadence
  // is fast enough that the LB1-2 90s soft-stall detector flips
  // within ~one tick of crossing the threshold without spinning the
  // event loop on idle pages.
  const [, setNowTick] = React.useState(0);
  const isInFlight = site.sedStatus === 'bootstrapping';
  React.useEffect(() => {
    if (!isInFlight) return;
    const id = window.setInterval(() => setNowTick(n => n + 1), 15_000);
    return () => window.clearInterval(id);
  }, [isInFlight]);

  // V134-BUCKET-REBUILD — in-flight backstop. The AuthGate
  // `runStuckBootstrapBackstop` only sweeps abandoned bootstraps ONCE at
  // post-auth, so a session that's already open when a run crosses the
  // 30-min ceiling would otherwise show the in-progress banner forever.
  // Mirror the backstop here, but ONLY for a genuinely ORPHANED run — past
  // the 30-min abandoned ceiling AND no live execution behind it (the hard
  // `isStuckBootstrap` no-progress signal). This is exactly the Holly Ridge
  // symptom: "0/4 · queued · started 34m ago" where the job already
  // terminated (succeeded on Cloud Run) but the client's sedStatus never
  // reconciled. A run that's merely absurdly long but STILL heartbeating is
  // left to the user's manual Cancel-and-retry (the RSF-3 path below) — we
  // don't auto-discard work that may yet land. One-shot per site via a ref
  // guard so the 15s tick can't re-fire it; force-abort no-ops on an
  // already-failed site.
  const abortedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isInFlight) {
      // Reset the guard once the site leaves 'bootstrapping' so a genuine
      // future rebuild on the same site can be backstopped again.
      if (abortedRef.current === site.id) abortedRef.current = null;
      return;
    }
    if (abortedRef.current === site.id) return;
    const nowMs = Date.now();
    const orphaned =
      isAbandonedBootstrap(site, nowMs) &&
      isStuckBootstrap(site, nowMs) &&
      shouldForceAbortBootstrap(site, nowMs);
    if (!orphaned) return;
    abortedRef.current = site.id;
    void (async () => {
      try {
        const store = useStore.getState();
        // RECONCILE-FIRST — re-read the site doc before force-failing. If the
        // backing job actually SUCCEEDED server-side but the local listener
        // went stale (token refresh / network blip / backgrounded tab), the
        // re-read flips sedStatus to complete/partial and we must NOT clobber
        // that with 'failed'. Only when the doc is STILL 'bootstrapping' after
        // the re-read is the run genuinely orphaned. Uses whichever re-read
        // seam the store exposes (refreshSite preferred, fetchSiteRagSites
        // fallback) — matching handleForceRefresh's resolution order.
        const refreshSite = (store as unknown as {
          refreshSite?: (id: string) => Promise<void>;
        }).refreshSite;
        const fetchSites = (store as unknown as {
          fetchSiteRagSites?: () => Promise<void>;
        }).fetchSiteRagSites;
        try {
          if (typeof refreshSite === 'function') {
            await refreshSite(site.id);
          } else if (typeof fetchSites === 'function') {
            await fetchSites();
          }
        } catch { /* re-read is best-effort; fall through to the post-read check */ }
        const afterRefresh = useStore.getState().siteRag.sites.find(s => s.id === site.id);
        if (!afterRefresh || !isAbandonedBootstrap(afterRefresh, Date.now()) || !isStuckBootstrap(afterRefresh, Date.now())) {
          // A completed job or newly observed live heartbeat is not abandoned.
          return;
        }
        const forceAbort = (store as unknown as {
          forceAbortSedRebuild?: (id: string) => Promise<void>;
        }).forceAbortSedRebuild;
        if (typeof forceAbort === 'function') {
          await forceAbort(site.id);
          try {
            const { emitAuditEvent } = await import('../../lib/auditLogClient');
            void emitAuditEvent({
              action: 'sed.rebuild_cancelled',
              target: { type: 'site', id: site.id, label: site.name },
              metadata: { source: 'in-flight-backstop', stage: site.sedRebuildProgress?.stage ?? 'unknown' },
            });
          } catch { /* audit best-effort */ }
        }
      } catch (e) {
        // Backstop is best-effort — never throw out of an effect.
        console.warn('[SedRebuildBanner] in-flight backstop failed:', (e as Error)?.message);
      }
    })();
    // Re-evaluate when the in-flight signal or its start stamps change.
  }, [isInFlight, site, site.id, site.sedStatusStartedAt, site.sedRebuildProgress?.startedAt]);

  // Two-click guard for the DESTRUCTIVE "Cancel and retry" — it discards an
  // in-progress build (provider fan-out + compile already paid). The first
  // click arms a confirm; the second within the window actually cancels. Auto-
  // disarms after 5s so a stray arm doesn't linger. "Force refresh" is NOT
  // guarded: it only re-reads Firestore (the cure for a stale frozen banner).
  const [armCancel, setArmCancel] = React.useState(false);
  React.useEffect(() => {
    if (!armCancel) return;
    const id = window.setTimeout(() => setArmCancel(false), 5_000);
    return () => window.clearTimeout(id);
  }, [armCancel]);

  // LB1-2 — "Cancel and retry" from the soft-stall banner. Logs the
  // event so product can measure how often the soft-stall surface is
  // the only path out of a frozen bootstrap (i.e. SD2+SD3 didn't
  // catch the underlying failure mode and the heartbeat went dark).
  const handleCancelAndRetry = async (sinceHeartbeatMs: number, stage: string | undefined) => {
    try {
      const { emitAuditEvent } = await import('../../lib/auditLogClient');
      void emitAuditEvent({
        action: 'sed.rebuild_cancelled',
        target: { type: 'site', id: site.id, label: site.name },
        metadata: {
          sinceHeartbeatMs,
          stage: stage ?? 'unknown',
          source: 'soft-stall-banner',
        },
      });
    } catch { /* audit is best-effort */ }
    try {
      // RSF-3 — If the previous run has exceeded the hard ceiling
      // (30 min), cooperatively cancel it BEFORE re-firing so the new
      // attempt doesn't stack on top of an already-doomed ghost run.
      // Below the ceiling the retry semantics are unchanged — rebuild
      // re-enters the standard `force-fail + dispatch` path which alone
      // is sufficient for normal sluggish-but-not-doomed runs. This
      // safety used to live only on the deleted red-tier Retry; it now
      // rides the surviving soft-stall "Cancel and retry" path.
      const store = useStore.getState();
      if (shouldForceAbortBootstrap(site, Date.now())) {
        const forceAbort = (store as unknown as {
          forceAbortSedRebuild?: (id: string) => Promise<void>;
        }).forceAbortSedRebuild;
        if (typeof forceAbort === 'function') {
          await forceAbort(site.id);
        }
      }
      await store.rebuildSiteSed(site.id);
    } catch (e) {
      toast.error(`Cancel-and-retry failed: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  };

  // LB1-2 — Force-refresh action. Re-reads the site from the store
  // without re-running the bootstrap. Useful when the Firestore
  // listener disconnects on the client (token refresh, network blip)
  // and the local state is stale even though the server-side write
  // already completed. The store's refreshSite re-fetches the site
  // doc; if absent, we fall back to a full re-fetch of the site list.
  const handleForceRefresh = async () => {
    try {
      const store = useStore.getState();
      const refreshSite = (store as unknown as { refreshSite?: (id: string) => Promise<void> }).refreshSite;
      if (typeof refreshSite === 'function') {
        await refreshSite(site.id);
      } else {
        // Back-compat fallback — fetch the full site list so the
        // listener disconnect can self-heal without a full refresh.
        const fetchSites = (store as unknown as { fetchSiteRagSites?: () => Promise<void> }).fetchSiteRagSites;
        if (typeof fetchSites === 'function') await fetchSites();
      }
      toast.success('Status refreshed', { id: `sed-refresh-${site.id}` });
    } catch (e) {
      toast.error(`Refresh failed: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  };

  if (!isInFlight) return null;

  const progress = site.sedRebuildProgress;
  const startedAt = progress?.startedAt ?? site.sedStatusStartedAt;
  const startedLabel = formatRelative(startedAt, Date.now());
  const lastProgressAtMs = progress?.lastProgressAt ? Date.parse(progress.lastProgressAt) : NaN;
  const lastProgressAgeMs = Number.isFinite(lastProgressAtMs) ? Math.max(0, Date.now() - lastProgressAtMs) : null;

  // LB1-2 — soft-stall (yellow) branch. Fires when the SD3 heartbeat
  // is stale (>HEARTBEAT_STALE threshold). With the red "appears stuck"
  // tier removed, this is the single in-flight escalation tier: even a
  // long-stuck (>10min) or >30-min ghost build surfaces here (it is
  // also heartbeat-stale), and the two-click Cancel-and-retry carries
  // the RSF-3 force-abort safety for the >30-min case.
  const nowMs = Date.now();
  if (isHeartbeatStale(site, nowMs)) {
    const lastProgressMs = progress?.lastProgressAt ? Date.parse(progress.lastProgressAt) : NaN;
    const sinceHeartbeatMs = Number.isFinite(lastProgressMs)
      ? Math.max(0, nowMs - lastProgressMs)
      : (startedAt ? Math.max(0, nowMs - Date.parse(startedAt)) : heartbeatStaleThresholdMs(site));
    const docTotal = (progress as { docTotal?: number } | undefined)?.docTotal;
    const isDocAwareWithManyDocs =
      progress?.stage === 'doc-aware' && typeof docTotal === 'number' && docTotal > 10;
    // C9b — soften the framing. The old "IDLE FOR {n}s" copy read as a
    // failure and pushed the user toward the destructive Cancel-and-retry
    // too eagerly. Large evidence sets + provider rate limits routinely
    // cause long quiet periods that are entirely normal, so the headline
    // now reassures and Force-refresh is the primary action; Cancel-and-
    // retry is demoted to a secondary ghost affordance gated behind a
    // two-click confirm. The RSF-3 force-abort-before-re-fire safety (for
    // a >30-min ghost run) now rides this surviving retry path, since the
    // red isStuckBootstrap tier that previously owned it was removed.
    const stallMessage = isDocAwareWithManyDocs
      ? `Extracting evidence from ${docTotal} documents — large evidence sets and provider rate limits can cause long quiet periods. You can keep waiting.`
      : 'SED rebuild is still working — large evidence sets and provider rate limits can cause long quiet periods. You can keep waiting.';
    const stallDone = progress?.done ?? 0;
    const stallTotal = progress?.total ?? 0;
    return (
      <div
        role="status"
        aria-live="polite"
        className="px-3 py-2 rounded-md border border-warning/40 bg-warning/5"
        data-testid="sed-rebuild-banner-soft-stall"
      >
        <div className="flex flex-wrap items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-warning shrink-0" />
          <div className="flex-1 min-w-[14rem] text-[10px] text-text-primary">
            <strong className="uppercase tracking-widest">SED rebuild is still working</strong>
            {' '}
            {stallMessage}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-6 type-body uppercase tracking-widest"
            onClick={() => void handleForceRefresh()}
            title="Safe — only re-reads the latest status. Does not interrupt the build."
            data-testid="sed-rebuild-banner-force-refresh"
          >
            Force refresh
          </Button>
          <Button
            variant={armCancel ? 'destructive' : 'ghost'}
            size="sm"
            className={`h-6 type-body uppercase tracking-widest ${armCancel ? '' : 'opacity-70'}`}
            onClick={() => {
              // First click arms; second click within 5s actually cancels.
              if (!armCancel) {
                setArmCancel(true);
                return;
              }
              setArmCancel(false);
              void handleCancelAndRetry(sinceHeartbeatMs, progress?.stage);
            }}
            title={
              armCancel
                ? 'Click again to discard the in-progress build and start over'
                : 'Discards the in-progress build and starts over — only if it is genuinely stuck'
            }
            data-testid="sed-rebuild-banner-cancel-retry"
          >
            {armCancel ? 'Confirm — discard progress' : 'Cancel and retry'}
          </Button>
        </div>
        <div className="mt-1.5">
          <SedProgressBar done={stallDone} total={stallTotal} tone="warning" />
        </div>
      </div>
    );
  }

  // KSD9 — when the heartbeat carries per-doc detail (set during
  // applyDocAwareConfidence), surface "Extracting from documents
  // (47 / 81) — Redhead_BTM.pdf → grid section" so the user has
  // a window into where the time goes. Falls back to the generic
  // "{done}/{total} stages complete" label for every other stage.
  // Cast because the per-doc detail fields live on the runtime
  // payload but the static type in src/types/site.ts is held by
  // the DR-NSW agent's worktree (KSD9 stitch step pending).
  const ksd9Detail = progress as (typeof progress & {
    currentDoc?: string;
    currentSection?: string;
    docIndex?: number;
    docTotal?: number;
  }) | undefined;
  const docDetail = ksd9Detail && ksd9Detail.currentDoc
    ? `Extracting from documents (${ksd9Detail.docIndex ?? '?'} / ${ksd9Detail.docTotal ?? '?'}) — ${ksd9Detail.currentDoc}${ksd9Detail.currentSection ? ` → ${ksd9Detail.currentSection}` : ''}`
    : null;
  // Human-readable stage labels so the banner conveys WHAT the build is doing,
  // not the raw machine stage token (e.g. 'fetch-fast' → "Collecting provider
  // data"). Keys mirror SedBootstrapStage in server/sedBootstrap/checkpoint.ts.
  const STAGE_LABELS: Record<string, string> = {
    queued: 'Queued — starting up',
    'fetch-fast': 'Collecting climate, grid, flood & parcel data',
    'fetch-fast-done': 'Provider data collected',
    'compile-infra': 'Compiling infrastructure evidence',
    compile: 'Compiling the evidence document',
    'doc-aware': 'Extracting from your documents',
    'conflict-detect': 'Reconciling conflicting values',
    'gcs-saved': 'Saving the evidence document',
    done: 'Complete',
  };
  const stageLabel = progress?.stage ? (STAGE_LABELS[progress.stage] ?? progress.stage) : null;
  const progressLabel = docDetail
    ?? (progress
      ? `Step ${Math.min(progress.done + 1, progress.total)} of ${progress.total}${stageLabel ? ` · ${stageLabel}` : ''}`
      : 're-querying providers');
  const shouldShowResumeHint =
    lastProgressAgeMs != null &&
    lastProgressAgeMs >= 30_000 &&
    lastProgressAgeMs < 90_000;
  const bannerLabel = shouldShowResumeHint
    ? `${progressLabel} · resuming from checkpoint…`
    : progressLabel;

  const inFlightDone = progress?.done ?? 0;
  const inFlightTotal = progress?.total ?? 0;
  const inFlightPct =
    inFlightTotal > 0 && inFlightDone > 0
      ? Math.min(100, Math.round((inFlightDone / inFlightTotal) * 100))
      : null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="px-3 py-2 border border-info/40 bg-info/5"
      data-testid="sed-rebuild-banner"
    >
      <div className="flex items-center gap-2">
        <RefreshCw className="w-3.5 h-3.5 animate-spin text-info shrink-0" />
        <div className="flex-1 text-[10px] text-text-primary">
          <strong className="uppercase tracking-widest">SED rebuild in progress</strong>
          {' · '}{bannerLabel}
          {startedAt ? ` · started ${startedLabel}` : ''}
        </div>
        {inFlightPct != null && (
          <span className="text-[10px] tabular-nums text-info shrink-0" data-testid="sed-rebuild-banner-pct">
            {inFlightPct}%
          </span>
        )}
      </div>
      <div className="mt-1.5">
        <SedProgressBar done={inFlightDone} total={inFlightTotal} tone="info" />
      </div>
    </div>
  );
};
